import { Button, Select, TextAreaInput, TextInput } from '@octano/global-ui';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import { useValidations } from '../../../../hooks/useValidations';
import { TranslationsKeys } from '../../../../locales/translations';
import {
  AdditionalChargeFields,
  AdditionalChargeFormData,
} from '../interfaces/types';

type AdditionalChargeFormProps = Partial<AdditionalChargeFormData> & {
  defaultValues?: Partial<AdditionalChargeFields>;
  saving?: boolean;
  onCancel?: () => void;
  onSubmit?: (values: AdditionalChargeFields) => void;
};

function AdditionalChargeForm({
  defaultValues,
  saving = false,
  onCancel,
  onSubmit,
  ...data
}: AdditionalChargeFormProps) {
  const { t } = useTranslation(
    TranslationsKeys.CONFIGURATION_ADDITIONAL_CHARGES,
  );
  const { msgValidations } = useValidations();

  const { control, ...form } = useForm<AdditionalChargeFields>({
    defaultValues,
  });

  const handleSubmit = useCallback(
    (values: AdditionalChargeFields) => {
      if (saving) {
        return;
      }
      !!onSubmit && onSubmit(values);
    },
    [saving, onSubmit],
  );

  return (
    <Row>
      <Col xs={12}>
        <TextInput
          control={control}
          name="name"
          label={t('fields.name')}
          rules={{ required: msgValidations.required }}
        />
      </Col>
      <Col xs={12} md={6}>
        <Select
          control={control}
          name="period"
          label={t('fields.period')}
          isClearable={false}
          options={data?.periods || []}
          rules={{ required: msgValidations.required }}
        />
      </Col>
      <Col xs={12} md={6}>
        <Select
          control={control}
          name="academicLevel"
          label={t('fields.academicLevel')}
          isClearable={false}
          options={data?.academicLevels || []}
          rules={{ required: msgValidations.required }}
        />
      </Col>
      <Col xs={12} md={6}>
        <TextInput
          control={control}
          name="amount"
          label={t('fields.amount')}
          rules={{ required: msgValidations.required }}
        />
      </Col>
      <Col xs={12} md={6}>
        <TextInput
          control={control}
          name="integrationCode"
          label={t('fields.integrationCode')}
          tooltip={t('fields.integrationCodeTooltip')}
        />
      </Col>
      <Col xs={12}>
        <TextAreaInput
          control={control}
          name="description"
          label={t('fields.description')}
        />
      </Col>
      <Col xs={12} lg={6} className="pb-2 mt-3 order-2 order-lg-1">
        <Button
          type="button"
          outlined
          disabled={saving}
          onClick={onCancel}
          text={t('common:actions.close')}
          fullwidth
        />
      </Col>
      <Col xs={12} lg={6} className="pb-2 mt-3 order-1 order-lg-2">
        <Button
          type="button"
          loading={saving}
          text={t('common:actions.save')}
          onClick={form.handleSubmit(handleSubmit)}
          fullwidth
        />
      </Col>
    </Row>
  );
}

export default AdditionalChargeForm;
